import { useMemo } from 'react';

import {
  InteractiveResponseTitle,
  InteractiveResponseAnswerIcons,
} from '../../shared/interactive-response-shared';
import {
  CATEGORISE_DROP_ITEM_SIZE,
  InteractiveResponseCategoriseProperties,
  RESPONSE_FREE,
} from '../interactive-response-categorise.constants';
import InteractiveDragDisplay from 'approot/activity-builder/components/shared/interactive-drag-drop/interactive-drag-display';
import { InteractiveResponseDropItemContainer } from '../../sequence/play/interactive-response-sequence.style';
import * as Styled from './interactive-response-categorise.style';
import { getAnswers } from './interactive-response-categorise-play.utils';
import {
  InteractiveCategoriseDropItemTarget,
  InteractiveDropItem,
  InteractiveDropItemTarget,
} from './interactive-response-categorise-drop.style';
import type { InteractiveResponseAnswerFragment } from '../../../../present/activity-answers.graphql.generated';
import { InteractiveDropDisplay } from './interactive-response-categorise-drop-display';

const InteractiveResponseCategorisePreviewAnswer = ({
  properties,
  attemptAnswers,
  showAnswer,
}: {
  properties: InteractiveResponseCategoriseProperties;
  attemptAnswers: InteractiveResponseAnswerFragment;
  showAnswer: boolean;
}) => {
  const isFreeResponse =
    properties.interactiveResponseCategorise.answerType === RESPONSE_FREE;
  const canShowAnswer = showAnswer && !isFreeResponse;

  const answers = useMemo(() => {
    return getAnswers(
      attemptAnswers,
      properties.interactiveResponseCategorise.items,
      properties.interactiveResponseCategorise.categories,
      canShowAnswer
    );
  }, [
    properties.interactiveResponseCategorise.items,
    properties.interactiveResponseCategorise.categories,
    attemptAnswers,
    canShowAnswer,
  ]);

  return (
    <>
      <InteractiveResponseTitle
        title={properties.interactiveResponseCategorise.title}
        subTitle={properties.interactiveResponseCategorise.subTitle}
      />
      <InteractiveResponseDropItemContainer>
        <Styled.InteractiveResponseCategories>
          {answers.map(answer => {
            return (
              <Styled.InteractiveResponseCategory key={answer.id}>
                <Styled.InteractiveResponseCategoryLabel>
                  {answer.label}
                </Styled.InteractiveResponseCategoryLabel>
                <InteractiveCategoriseDropItemTarget>
                  <Styled.InteractiveResponseCategoryRow>
                    {answer.zones.map(zone => {
                      const droppedDragItem =
                        properties.interactiveResponseCategorise.items.find(
                          item => item.itemId === zone.dragItemId
                        );
                      const variant =
                        droppedDragItem?.variant ||
                        properties.interactiveResponseCategorise.variant;
                      return (
                        <InteractiveDropItem key={zone.dragItemId}>
                          <InteractiveDropItemTarget $fullHeight $fullWidth>
                            {canShowAnswer && (
                              <InteractiveResponseAnswerIcons
                                isCorrect={zone.isCorrect || false}
                                animate={false}
                              />
                            )}
                            <InteractiveDropDisplay
                              isContinuousMarking={false}
                              isCorrect={
                                !canShowAnswer ? undefined : zone.isCorrect
                              }
                              hasDropped
                            >
                              <InteractiveDragDisplay
                                scaleTo={CATEGORISE_DROP_ITEM_SIZE}
                                imageUrl={zone.previewItemId}
                                isFit={droppedDragItem?.imageMode === 'fit'}
                                description={
                                  droppedDragItem?.image?.description || ''
                                }
                                baseColour={droppedDragItem?.baseColour || null}
                                text={droppedDragItem?.text || null}
                                richtext={droppedDragItem?.richtext || null}
                                variant={variant}
                              />
                            </InteractiveDropDisplay>
                          </InteractiveDropItemTarget>
                        </InteractiveDropItem>
                      );
                    })}
                  </Styled.InteractiveResponseCategoryRow>
                </InteractiveCategoriseDropItemTarget>
              </Styled.InteractiveResponseCategory>
            );
          })}
        </Styled.InteractiveResponseCategories>
      </InteractiveResponseDropItemContainer>
    </>
  );
};

export default InteractiveResponseCategorisePreviewAnswer;
